import React from 'react';

export interface AuthSplitLayoutProps {
  /** Main marketing body (badge, title, copy, highlights, feature list, etc.) */
  hero: React.ReactNode;
  /** Bottom strip inside the left pane (trust notes) */
  footer: React.ReactNode;
  /** Full right column: usually `AuthFormCard` + contents */
  children: React.ReactNode;
}

/**
 * Shared split auth shell: branded left narrative + form column on the right.
 * Matches login responsive behavior (scroll on small screens, fixed split on lg+).
 */
export const AuthSplitLayout: React.FC<AuthSplitLayoutProps> = ({ hero, footer, children }) => (
  <div className="flex min-h-screen flex-col bg-slate-100/80 lg:h-screen lg:max-h-screen lg:overflow-hidden lg:flex-row lg:bg-transparent">
    <style>{`
      @keyframes authFadeInUp {
        from { opacity: 0; transform: translateY(30px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes authSlideInLeft {
        from { opacity: 0; transform: translateX(-30px); }
        to { opacity: 1; transform: translateX(0); }
      }
      .auth-fade-in-up { animation: authFadeInUp 0.8s ease-out; }
      .auth-slide-in-left { animation: authSlideInLeft 0.8s ease-out; }
      .auth-slide-in-left-delay-1 { animation: authSlideInLeft 0.8s ease-out 0.2s both; }
      .auth-slide-in-left-delay-2 { animation: authSlideInLeft 0.8s ease-out 0.4s both; }
    `}</style>

    <div
      className="relative flex w-full shrink-0 overflow-hidden lg:h-full lg:min-h-0 lg:w-[52%] xl:w-1/2 lg:flex-none lg:overflow-hidden"
      style={{ backgroundColor: '#0171BF' }}
    >
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(145deg, rgba(1, 53, 96, 0.92) 0%, rgba(1, 113, 191, 0.88) 45%, rgba(247, 148, 31, 0.72) 100%)',
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      <div className="relative z-10 flex min-h-0 w-full flex-col justify-between gap-6 px-5 py-10 text-white sm:px-8 sm:py-12 md:px-10 md:py-14 lg:h-full lg:gap-5 lg:px-12 lg:py-9 xl:px-14 xl:py-10">
        <div className="flex min-h-0 flex-1 flex-col justify-center lg:max-w-lg xl:max-w-xl">{hero}</div>
        <div className="auth-slide-in-left mt-1 flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-t border-white/15 pt-4 text-[11px] leading-relaxed text-white/80 sm:gap-x-5 sm:pt-5 sm:text-xs">
          {footer}
        </div>
      </div>

      <div className="pointer-events-none absolute top-24 -right-8 h-72 w-72 rounded-full bg-[#F7941F]/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-16 left-1/4 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
    </div>

    {children}
  </div>
);
